// React imports
import React, { useContext, useEffect, useMemo, useState } from "react";

// Context imports
import { AccessTokenContext } from "../../../contexts/AccessTokenContext";

// UI elements imports
import DisplayBox from "../../../ui-elements/DisplayBox/DisplayBox";
import FileUploadDropzone from "../../../ui-elements/FileUploadDropZone/FileUploadDropZone";

// Modal imports
import RiskScoreModal from "../RiskScoreModal/RiskScoreModal";

// Component imports
import CompanyAnswersList from "./Components/CompanyAnswers";
import CompanyTitle from "../../Company/CompanyTitle/CompanyTitle";
import GildandiSkraningAdilar from "./Components/GildandiSkraningAdilar";
import GildandiSkraningGrunnupplysingar from "./Components/GildandiSkraningGrunnupplysingar";
import QuestionaireResults from "./Components/QuestionaireResults";
import AlthjodlegirValistar from "./Components/AlthjodlegirValistar";
import KYCNavBar from "../../Company/KYCNavBar/KYCNavBar";
import InfoHeader from "../../../ui-elements/InfoHeader/InfoHeader";
import CompanyTitleLoadingSkeleton from "../../Company/CompanyTitle/CompanyTitleLoadingSkeleton";
import PepWatchPEPConnectionModal from "../../PEP/PepWatchModals/PepWatchPEPConnectionModal";
import GoBackLink from "../../../ui-elements/GoBackLink/GoBackLink";
import EditContactModal from "../EditContactModal/EditContactModal";
import ForeignPepModal from "../../PEP/ForeignPepModal";

// Type imports
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import { AnswerListOverviewDto, ForeignPepReport } from "../../../types/CompanyAnswersTypes";
import { ContactsOverview, IAdilarTableData, RiskScoreDto, RiskScoreFormValues } from "../../../types/DueDiligenceTypes";

// 3rd party imports
import { Toast, ToastContainer } from "react-bootstrap";
import Bugsnag from "@bugsnag/js";

// Config imports
import { TOAST_DELAY } from "../../../services/config";

//static data imports
import { CountryList } from "../../../assets/staticData/CountryList";
import { PepResult } from "../../../types/PepTypes";
import { IPepArray } from "../../PEP/PepPage";

const ViewAnswersPage: React.FC<{ customerId: string; riskscoreId: string; riskscore: RiskScoreDto, answerListId: number }> = ({
    customerId,
    riskscoreId,
    riskscore,
    answerListId
}) => {
    const accessToken = useContext(AccessTokenContext);

    //TODO: ADD ERROR TO THIS 
    const [data, setData] = useState<AnswerListOverviewDto | undefined | null | Error>(undefined);
    const [riskScore, setRiskScore] = useState<RiskScoreDto>(riskscore);
    const [riskScoreModal_isOpen, setRiskScoreModal_isOpen] = useState(false);
    const [riskScoreModal_data, setRiskScoreModal_data] = useState<RiskScoreFormValues | undefined>(undefined);
    const [answerListIndex, setAnswerListIndex] = useState<number>(0);

    const [editContactModal_isOpen, setEditContactModal_isOpen] = useState(false);
    const [editContactModal_data, setEditContactModal_data] = useState<{ customerId: number; companyName: string; confirmProcurations: boolean }>({ customerId: 0, companyName: '', confirmProcurations: false });
    const [editContactModal_contacts, setEditContactModal_contacts] = useState<ContactsOverview | null>(null);

    const [foreignPepModal_isOpen, setForeignPepModal_isOpen] = useState(false);
    const [foreignPepModal_data, setForeignPepModal_data] = useState<PepResult[] | undefined>(undefined);

    const [pepModal_isOpen, setPepModal_isOpen] = useState(false);
    const [pepModal_data, setPepModal_data] = useState<IPepArray[] | undefined>(undefined);

    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>("success");
    const [toastMessage, setToastMessage] = useState("");

    useEffect(() => {
        setData(null); // Set to loading state
        const fetchData = async () => {
            try {
                const response = await fetch(`/DueDiligence/GetKYCAnswers?riskscoreId=${riskscoreId}&customerId=${customerId}`);
                const data: AnswerListOverviewDto = await response.json();
                if (!response.ok) {
                    throw new Error('Villa við að sækja gögn um áreiðanleikakönnun.');
                }
                setData(data);
            } catch (e) {
                if (e instanceof Error) {
                    Bugsnag.notify(e);
                    setData(e);
                }
            }
        }
        fetchData();
    }, [riskscoreId, customerId]);

    useEffect(() => {
        if (answerListId && data && !(data instanceof Error)) {
            const index = data.answerLists.findIndex(answerList => answerList.answerlistId === answerListId);
            if (index !== -1) {
                setAnswerListIndex(index);
            }
        }
    }, [data, answerListId]);

    useEffect(() => {
        if (!data || data instanceof Error) return;
        setEditContactModal_data({
            customerId: data.customerId,
            companyName: data.customerName,
            confirmProcurations: data.answerLists[answerListIndex]?.hasProcurationVerification ?? false
        });
    }, [data, answerListIndex]);

    const isConnectedStjornmalalegt = (name: string) => {
        if (data === null || data instanceof Error) return undefined;
        const hasResults = data?.pepReport?.results?.find(entry => entry.name === name);
        if (!hasResults) return undefined;
        return hasResults;
    }

    const isConnectedAlthjodlegirValistar = (name: string) => {
        if (data === null || data instanceof Error) return undefined;
        let results: ForeignPepReport[] | undefined;
        const nameParts = name.split(' ');
        const firstName = nameParts[0];
        const lastName = nameParts.length > 1 ? nameParts.slice(-1)[0] : undefined;
        const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : undefined;

        // Check first name
        results = data?.foreignPepReports?.filter(entry => entry.mappedReport?.request?.firstName === firstName);
        // If more than 1 person with first name, check last name if available
        if (results && results.length > 1 && lastName) {
            results = results.filter(entry => entry.mappedReport?.request?.lastName === lastName);
        }
        // If more than 1 person with first and last name, check middle name if available
        if (results && results.length > 1 && middleName) {
            results = results.filter(entry => entry.mappedReport?.request?.middleName === middleName);
        }
        if (!results || results.length === 0) return undefined;
        return results;
    }

    // useMemo for adilar table data
    const adilarTableData = useMemo(() => {
        if (data === null || data instanceof Error || data === undefined) return [];
        const dataToReturn: IAdilarTableData[] = [];
        const althChecked: ForeignPepReport[] = [];
        if (data.keldanReport?.mappedReport?.prokuruhafar?.length > 0) {
            data.keldanReport.mappedReport.prokuruhafar.forEach((member) => {
                const stjornResults = isConnectedStjornmalalegt(member.nafn);
                const althResults = isConnectedAlthjodlegirValistar(member.nafn);
                if (althResults) althChecked.push(althResults[0]);
                const procurationVerification = data?.procurers?.find(procurer => procurer.ssn === member.kennitala);
                dataToReturn.push({
                    hlutverk: "Prókúruumboð",
                    nafnadila: member.nafn,
                    kennitala: member.kennitala,
                    rikisfang: CountryList.find(country => country.value === member.nationality)?.label ?? '',
                    framvisunskilr: (procurationVerification?.eLoginTimestamp !== null && procurationVerification?.eLoginTimestamp !== undefined && procurationVerification?.eLoginTimestamp !== "")
                        ? procurationVerification.eLoginTimestamp : procurationVerification?.notificationSent ? "Beiðni send" : "",
                    stjornmalalegtStatus: stjornResults?.connected,
                    stjornmalalegtData: stjornResults,
                    althjodlegirvalistarStatus: althResults?.[0].hasResults,
                    althjodlegirvalistarData: althResults?.[0],
                    ordsporsahaetta: '-',
                    dateOfBirth: member.faedDagur,
                    //if heimiliFatfCountry is null then we didnt find the country in our list.
                    //else it would be true or false
                    // from that we can deduce that heimili is very likely iceland
                    // if postnr is not empty string or null then its likely iceland
                    // also if postfang is not empty string or null then VERY likely iceland
                    busetuland: (member.heimiliFatfCountry === null ||
                        member.postnr !== '' || member.postnr !== null ||
                        member.postfang !== '' || member.postfang !== null) ? 'Ísland' : member.heimili,
                    busetulandFatfCountry: member.heimiliFatfCountry,
                    nationalityFatfCountry: member.nationalityFatfCountry,

                });
            });
        }
        if (data.keldanReport?.mappedReport?.owners?.length > 0) {
            data.keldanReport.mappedReport.owners.forEach((member) => {
                if (!dataToReturn.find((adili) => member.nafn === adili.nafnadila)) {
                    const stjornResults = isConnectedStjornmalalegt(member.nafn);
                    const althResults = isConnectedAlthjodlegirValistar(member.nafn);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Raunverulegur eigandi",
                        nafnadila: member.nafn,
                        kennitala: member.ssn,
                        rikisfang: member.rikisfang,
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0].hasResults,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '-',
                        dateOfBirth: member.faedingarmanudur,
                        busetuland: member.busetuland,
                        busetulandFatfCountry: member.busetulandFatfCountry,
                        nationalityFatfCountry: member.nationalityFatfCountry,
                    });
                }
            });
        }
        if (data.keldanReport?.mappedReport?.contacts?.length > 0) {
            // Contact that answered the questionnaire
            if (data.answerLists.length > 0) {
                const answerContact = data.keldanReport?.mappedReport.contacts?.find(contact => contact.name === data.answerLists[answerListIndex].userName);
                if (answerContact && !dataToReturn.find((adili) => answerContact.name === adili.nafnadila)) {
                    const stjornResults = isConnectedStjornmalalegt(answerContact.name);
                    const althResults = isConnectedAlthjodlegirValistar(answerContact.name);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: answerContact.name,
                        kennitala: answerContact.ssn,
                        rikisfang: CountryList.find(country => country.value === answerContact.nationality)?.label ?? '',
                        framvisunskilr: data.answerLists[answerListIndex].userELoginTimestamp,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0].hasResults,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '-', // not implemented yet
                        busetuland: answerContact?.heimiliFatfCountry === null ||
                            answerContact?.areaCode !== '' || answerContact?.areaCode !== null ||
                            answerContact?.region !== '' || answerContact?.region !== null ? 'Ísland' : answerContact.address,
                        busetulandFatfCountry: answerContact?.heimiliFatfCountry,
                        nationalityFatfCountry: answerContact?.nationalityFatfCountry,
                    });
                } else if (!dataToReturn.find((adili) => data.answerLists[answerListIndex].userName === adili.nafnadila)) {
                    // Fallback if contact not found in report contacts
                    const stjornResults = isConnectedStjornmalalegt(data.answerLists[answerListIndex].userName);
                    const althResults = isConnectedAlthjodlegirValistar(data.answerLists[answerListIndex].userName);
                    if (althResults) althChecked.push(althResults[0]);
                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: data.answerLists[answerListIndex].userName,
                        kennitala: data.answerLists[answerListIndex].userSSN,
                        rikisfang: '',
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: stjornResults?.connected,
                        stjornmalalegtData: stjornResults,
                        althjodlegirvalistarStatus: althResults?.[0].hasResults,
                        althjodlegirvalistarData: althResults?.[0],
                        ordsporsahaetta: '', // not implemented yet
                        busetuland: answerContact?.heimiliFatfCountry === null ||
                            answerContact?.areaCode !== '' || answerContact?.areaCode !== null ||
                            answerContact?.region !== '' || answerContact?.region !== null ? 'Ísland' : answerContact.address,
                        busetulandFatfCountry: '',
                        nationalityFatfCountry: undefined,
                    });
                }
            } else {
                // No answer, so we take contact currently assigned from report
                const activeContact = data.keldanReport?.mappedReport?.contacts.find(contact => contact.name === data.contact.name);
                if (activeContact && !dataToReturn.find((adili) => activeContact.name === adili.nafnadila)) {
                    const rikisfang = (activeContact.nationality === null ||
                        activeContact.areaCode !== '' || activeContact.areaCode !== null ||
                        activeContact.region !== '' || activeContact.region !== null)
                        ? 'Ísland'
                        : CountryList.find(country => country.value === activeContact.nationality)?.label || '';
                    const busetuland = (activeContact.heimiliFatfCountry === null ||
                        activeContact.areaCode !== '' || activeContact.areaCode !== null ||
                        activeContact.region !== '' || activeContact.region !== null) ? 'Ísland' : activeContact.address;

                    dataToReturn.push({
                        hlutverk: "Tengiliður",
                        nafnadila: activeContact.name,
                        kennitala: activeContact.ssn ?? '',
                        rikisfang: rikisfang,
                        framvisunskilr: undefined,
                        stjornmalalegtStatus: undefined, // won't have this if no answer
                        althjodlegirvalistarStatus: undefined, // won't have this if no answer
                        ordsporsahaetta: '-',
                        nationalityFatfCountry: activeContact.nationalityFatfCountry,
                        //These are only needed for prokurors and owners so we can leave them empty here
                        busetuland: busetuland,
                        busetulandFatfCountry: activeContact.heimiliFatfCountry,
                    });
                }
            }
        } else if (data.contact) {
            // No report, so we use assigned contact
            if (!dataToReturn.find((adili) => data.contact.name === adili.nafnadila)) {
                const stjornResults = isConnectedStjornmalalegt(data.contact.name);
                const althResults = isConnectedAlthjodlegirValistar(data.contact.name);
                if (althResults) althChecked.push(althResults[0]);
                const busetuland = (data.contact.country === null ||
                    data.contact.areaCode !== '' || data.contact.areaCode !== null ||
                    data.contact.region !== '' || data.contact.region !== null) ? 'Ísland' : data.contact.address;
                const rikisfang = (data.contact.country === null ||
                    data.contact.areaCode !== '' || data.contact.areaCode !== null ||
                    data.contact.region !== '' || data.contact.region !== null)
                    ? 'Ísland'
                    : CountryList.find(country => country.value === data.contact.country)?.label || '';


                dataToReturn.push({
                    hlutverk: "Tengiliður",
                    nafnadila: data.contact.name,
                    kennitala: data.contact.ssn ?? '',
                    rikisfang: rikisfang,
                    framvisunskilr: undefined,
                    stjornmalalegtStatus: stjornResults?.connected,
                    stjornmalalegtData: stjornResults,
                    althjodlegirvalistarStatus: althResults?.[0].hasResults,
                    althjodlegirvalistarData: althResults?.[0],
                    ordsporsahaetta: '',
                    //These are only  for prokurors and owners so we can leave them empty here
                    busetuland: busetuland,

                    //TODO: fix this properly
                    // WE HAVE NO DATA FOR FATF COUNTRY HERE
                    nationalityFatfCountry: '',
                    busetulandFatfCountry: '',
                });
            }
        }
        return dataToReturn;
    }, [data, answerListIndex]);
    const renderBody = () => {
        if (data instanceof Error) return <Alert type="error" text={data.message} headText={data.message} />;
        else if (data === undefined) return <Alert type="error" headText="Villa við að sækja gögn um áreiðanleikakönnun." />
        return <>
            {
                // Data is null: we haven't fetched yet, show loading
                data === null ? (
                    <CompanyTitleLoadingSkeleton />
                ) : // Data is not null: we have fetched, but customerSSN might be null
                    data.customerSSN === null || data.customerSSN === undefined ? (
                        // Show fallback CompanyTitle without an ID (or with a different fallback UI)
                        <InfoHeader data={[{
                            title: "Fyrirtæki",
                            value: data.customerName || "Nafn ekki til staðar"
                        }, {
                            title: "Land",
                            value: CountryList.find(country => country.value === data?.customerCountry)?.label || 'Land ekki til staðar'
                        }]} />
                    ) : (
                        // We have data and customerSSN, show CompanyTitle normally
                        <CompanyTitle disableMargin showLoading id={data.customerSSN} accessToken={accessToken} />
                    )
            }
            {/* <CompanyTitle showLoading id={data?.customerSSN} accessToken={accessToken}/> */}
            {/* questionaire-results / questionaire-overview */}
            {
                data?.answerLists && data.answerLists.length > 0 && <QuestionaireResults
                    riskScore={riskScore}
                    setRiskScoreModal_isOpen={setRiskScoreModal_isOpen}
                    setRiskScoreModal_data={setRiskScoreModal_data}
                    customerName={data.customerName} />
            }

            {/* Error state */}
            {data?.errorState && <Alert type="error" headText="Villa" text={data?.errorMessage ?? ""} />}

            {/* Gildandi skráning - aðilar */}
            {riskscoreId !== null &&
                <GildandiSkraningAdilar
                    customerId={data?.customerId}
                    adilarTableData={adilarTableData}
                    riskScore={riskScore}
                    showLoading={data?.errorState || adilarTableData?.length === 0}
                    setEditContactModal_contacts={setEditContactModal_contacts}
                    setEditContactModal_isOpen={setEditContactModal_isOpen}
                    setForeignPepModal_data={setForeignPepModal_data}
                    setForeignPepModal_isOpen={setForeignPepModal_isOpen}
                    setPepModal_data={setPepModal_data}
                    setPepModal_isOpen={setPepModal_isOpen}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                />
            }
            {/* Gildandi skráning - grunnupplýsingar (open by default if errorState) */}
            <GildandiSkraningGrunnupplysingar
                data={data}
                initialOpen={!!data?.errorState || riskscoreId === null}
            />

            {/* Svör við áreiðanleikakönnun hjá Kóði ehf. */}
            {data?.answerLists && data.answerLists.length > 0 && <CompanyAnswersList answerListIndex={answerListIndex} data={data} />}

            {/* Alþjóðlegir válistar */}
            {(!data?.errorState || riskscoreId !== null) && <AlthjodlegirValistar
                searchHistory={data?.foreignCompanyPepReports ?? []}
                riskscoreId={riskscoreId}
                initialName={data?.customerName ?? ''}
                initialCountryCode={'IS'} />}

            {/* Fylgiskjöl. */}
            {
                !data?.errorState &&
                data?.answerLists[answerListIndex] && data.answerLists[answerListIndex].answerlistId &&
                <DisplayBox id="file-upload" title="Fylgiskjöl">
                    <p className="paragraph-small--bold instruction-text">Settu inn allt að 10 skjöl:</p>
                    <FileUploadDropzone initialFiles={data.answerLists[answerListIndex].serviceCompanyFiles} answerListId={data.answerLists[answerListIndex].answerlistId} />
                </DisplayBox>
            }</>
    }

    return (
        <>
            <div className="KCL_DueDiligence-ViewCompanyAnswers">
                <KYCNavBar />
                <div className="ViewCompanyAnswers__content">
                    <div className="shell page_content">
                        <div className="section__header">
                            <div className="section__header--left">
                                <GoBackLink fallbackUrl="/Areidanleikakannanir/Yfirlit" />
                                <h1 title="Áreiðanleikakönnun">
                                    Áreiðanleikakönnun
                                </h1>
                            </div>
                            {/*<div className="section__header--right red-wrapper">
                                <GeniusLabel active={true} />
                                <Button size="sm" buttonType="secondary" icon={faEdit}>Breyta</Button>
                            </div>*/}
                        </div>
                        {renderBody()}
                    </div >
                </div>
                {/* Risk Score Modal */}
                <RiskScoreModal
                    show={riskScoreModal_isOpen}
                    close={() => setRiskScoreModal_isOpen(false)}
                    data={riskScoreModal_data}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    riskscore={riskScore}
                    setRiskscore={setRiskScore}
                />
                {/* Edit Contact Modal */}
                <EditContactModal
                    show={editContactModal_isOpen}
                    close={() => setEditContactModal_isOpen(false)}
                    data={editContactModal_data}
                    contacts={editContactModal_contacts}
                    setToastShow={setToastShow}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    sendNotification={true}
                    riskScore={riskScore}
                    setRiskScore={setRiskScore}
                    employees={editContactModal_contacts?.employees ?? []}
                />
                <ForeignPepModal
                    show={foreignPepModal_isOpen}
                    close={() => setForeignPepModal_isOpen(false)}
                    data={foreignPepModal_data}
                />
                <PepWatchPEPConnectionModal
                    show={pepModal_isOpen}
                    close={() => setPepModal_isOpen(false)}
                    fetchedData={pepModal_data}
                />
            </div >
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </>
    )
};

export default ViewAnswersPage;
